import { t } from "i18next";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import BackEndService from "../../api/BackEndService";
import CustomerService from "../../api/CustomerService";
import PropertyService from "../../api/PropertyService";
import { AppLayout } from "../../layout/AppLayout/AppLayout";
import { generateUUIDFromID } from "../../services/UUIDGeneratorService";
import { RootState } from "../../store";
import { searchActions } from "../../store/search-slice";
import { creatingTaskActions } from "../../store/task-slice";
import { SevDeskCustomer } from "../../types/customer";
import { sortCustomers } from "../../utils/sortCustomers";
import { List } from "../List/List";
import PullToRefresh from "../PullToRefresh/PullToRefresh";
import { SubMenu } from "../SubMenu/SubMenu";

import styles from "./task-form.module.css";

export const StepOne = (): JSX.Element => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [initialCustomers, setInitialCustomers] = useState([]);
  const [isNavigating, setIsNavigating] = useState(false);
  const searchValue = useSelector((state: RootState) => state.search.value);

  // Fetch fresh list of sevdesk customers
  useEffect(() => {
    fetchCustomers();
  }, []);

  // Fetch sevdesk customers list
  const fetchCustomers = async () => {
    try {
      const customers = await CustomerService.GetSevDeskCustomers();

      const sortedCustomers = sortCustomers(customers);

      setCustomers(sortedCustomers);
      setInitialCustomers(sortedCustomers);
    } catch (error) {
      console.log(error);
    }
  };

  // Fetch customer properties by customer id
  const fetchCustomerProperties = async (customerId: string) => {
    const _customerId = generateUUIDFromID(customerId?.toString()!);

    try {
      const sevdeskProperties = await BackEndService.GetObjects(customerId); //sevdesk objects
      const properties = await PropertyService.GetProperties({
        "filter[customerId]": _customerId,
      }); //backend objects

      // Set customer properties that we need to list on step two
      dispatch(
        creatingTaskActions.setCustomerProperties({
          customerProperties: [...sevdeskProperties.data, ...properties.data],
        })
      );

      navigate("/customerinsight/second-step");
    } catch (error) {
      console.log(error);
    }
  };

  const onSelectCustomer = (customer: Customer) => {
    setIsNavigating(true);
    dispatch(creatingTaskActions.setCustomer({ customer: customer }));
    dispatch(
      creatingTaskActions.setAdditionalDataCustomer({
        customer: {
          id: customer.id,
          name: customer.name,
        },
      })
    );

    fetchCustomerProperties(customer.id);

    dispatch(searchActions.setIsExpanded(false));
    dispatch(searchActions.setSearchValue(""));
  };

  const handlePullToRefresh = (callback: Function) => {
    fetchCustomers()
      .then((customers) => {
        callback();
      })
      .catch((error) => {
        callback(error);
      });
  };

  const handleGoBack = () => {
    dispatch(creatingTaskActions.resetAppointment());
    navigate("/");
  };

  useEffect(() => {
    if (!isNavigating) {
      if (searchValue.length >= 3) {
        const lowerCaseSearchValue = searchValue.toLowerCase();

        setCustomers(
          initialCustomers.filter(
            (customer: SevDeskCustomer) =>
              customer.name?.toLowerCase().includes(lowerCaseSearchValue) ||
              customer.surename?.toLowerCase().includes(lowerCaseSearchValue) ||
              customer.familyname
                ?.toLowerCase()
                .includes(lowerCaseSearchValue) ||
              (customer.surename + " " + customer.familyname)
                .toLowerCase()
                .includes(lowerCaseSearchValue)
          )
        );
      } else if (searchValue.length === 0) {
        setCustomers(initialCustomers);
      }
    }
  }, [searchValue, initialCustomers, isNavigating]);

  return (
    <AppLayout
      className={styles["tasks__list"]}
      withBackButton={true}
      onGoBack={handleGoBack}
    >
      <SubMenu showSearchButton={true}>{t("EnterTask")}</SubMenu>
      <PullToRefresh
        className={styles["customer-refresh"]}
        onRefresh={handlePullToRefresh}
      >
        <List
          subtitle={t("CustomerSelect")}
          data={customers.map((customer: SevDeskCustomer) => ({
            id: customer.id.toString(),
            name:
              customer.name || customer.surename + " " + customer.familyname,
          }))}
          onItemSelected={(customer: any) => {
            onSelectCustomer(customer);
          }}
        />
      </PullToRefresh>
    </AppLayout>
  );
};
